import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Stack, Tooltip, Typography, useTheme, ClickAwayListener } from '@mui/material';

import { Close } from '../../icons';
import { main300Color, main400Color } from '../tokenColorTemporary';

import type { Theme, TooltipProps } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';
import useAIDTMediaQuery from '../../hooks/useAIDTMediaQuery';

export interface EliceTooltipProps extends TooltipProps {
  title: string | ReactNode;
  isControl?: boolean;
  children: ReactElement<any, any>;
  initOpen?: boolean;
  triggerAction?: 'default' | 'click';
  backgroundColor?: string;
  closeColor?: string;
  fontColor?: string;
  closeOnScroll?: boolean;
  slotStyles?: React.CSSProperties;
  allowMultiple?: boolean;
  maxHeight?: string;
}

const StyledScrollBar = styled(Typography)`
  ::-webkit-scrollbar {
    width: 0.5rem;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    display: none;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }: { theme: Theme }) => theme.palette.primary.main};
    border-radius: 0.5rem;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }: { theme: Theme }) => theme.palette.primary.main};
  }
`;

const EliceTooltip = ({
  title,
  initOpen = false,
  isControl,
  placement = 'left',
  triggerAction = 'default',
  children,
  backgroundColor,
  closeColor,
  fontColor,
  closeOnScroll = false,
  slotStyles,
  allowMultiple = false,
  maxHeight = '16rem',
  ...rest
}: EliceTooltipProps) => {
  const theme = useTheme();
  const { isTablet, isHeightSmall } = useAIDTMediaQuery();
  const triggerActionTry =
    triggerAction === 'default' && (isTablet || isHeightSmall) ? 'click' : triggerAction;
  const slotProps = {
    tooltip: {
      style: {
        backgroundColor: backgroundColor || theme.palette.primary.main,
        color: fontColor || '#fff',
        borderRadius: '1rem',
        padding: '0.75rem',
        maxWidth: '15.7rem',
        whiteSpace: 'pre-wrap',
        ...slotStyles,
      },
    },
    arrow: {
      style: {
        color: backgroundColor || theme.palette.primary.main,
        fontSize: '0.75rem',
      },
    },
  };

  const [open, setOpen] = useState(false);
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setOpen(initOpen);
      return;
    }
  }, [initOpen]);

  React.useEffect(() => {
    const handleScroll = () => {
      setOpen(true);
    };

    if (open && closeOnScroll) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, closeOnScroll]);

  // allowMultiple이 true일 때는 MUI Tooltip의 기본 동작(다른 툴팁 열릴 때 자동 닫힘)을 비활성화
  // MUI Tooltip은 기본적으로 다른 툴팁이 열릴 때 자동으로 닫히는데, 이를 방지하기 위해
  // onClose 핸들러에서 allowMultiple이 true일 때는 닫히지 않도록 처리
  const handleClose = React.useCallback(
    (event: Event | React.SyntheticEvent) => {
      // allowMultiple이 true일 때는 다른 툴팁이 열려도 닫히지 않도록 함
      // 단, 사용자가 명시적으로 닫는 경우(Close 버튼 클릭, ClickAway 등)는 닫힘
      if (!allowMultiple) {
        setOpen(false);
      }
      // allowMultiple이 true일 때는 onClose에서 아무것도 하지 않음
      // (다른 툴팁이 열려도 현재 툴팁이 닫히지 않음)
    },
    [allowMultiple]
  );

  return isControl ? (
    <ClickAwayListener onClickAway={() => setOpen(false)} mouseEvent="onClick">
      <Tooltip
        arrow
        title={
          <Stack direction="row" gap="0.625rem">
            <Close
              style={{
                opacity: 0.7,
                flexShrink: 0,
                cursor: 'pointer',
              }}
              color={closeColor || 'currentColor'}
              onClick={() => setOpen(false)}
            />
            <StyledScrollBar
              style={{
                maxHeight: maxHeight,
                overflowY: 'auto',
              }}
              theme={theme}
            >
              <Typography variant="subtitle2">{title}</Typography>
            </StyledScrollBar>
          </Stack>
        }
        placement={placement}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={handleClose}
        onClick={() => {
          setOpen(true);
        }}
        slotProps={slotProps}
        style={{
          cursor: 'pointer',
        }}
        {...rest}
      >
        <Stack
          position="relative"
          width="fit-content"
          height="fit-content"
          whiteSpace="pre-wrap"
        >
          {children}
        </Stack>
      </Tooltip>
    </ClickAwayListener>
  ) : (
    <ClickAwayListener onClickAway={() => setOpen(false)} mouseEvent="onClick">
      <Tooltip
        arrow
        title={
          <StyledScrollBar
            style={{
              fontWeight: 700,
              fontSize: '1.375rem',
              maxHeight: '15rem',
              overflowY: 'auto',
            }}
            theme={theme}
          >
            <Typography variant="subtitle2">{title}</Typography>
          </StyledScrollBar>
        }
        placement={placement}
        slotProps={slotProps}
        onClose={handleClose}
        {...rest}
      >
        <Stack position="relative" width="fit-content" height="fit-content">
          {children}
        </Stack>
      </Tooltip>
    </ClickAwayListener>
  );
};
export default EliceTooltip;
